import type { SubmissionRow } from '../../types/submissions';

const FORM_TYPE_LABELS: Record<string, string> = {
  collateral: 'Collateral',
  'trade-show': 'Trade Show',
  'quick-note': 'Quick Note',
};

const FORM_TYPE_COLORS: Record<string, string> = {
  collateral: 'bg-blue-100 text-blue-700',
  'trade-show': 'bg-purple-100 text-purple-700',
  'quick-note': 'bg-green-100 text-green-700',
};

interface SubmissionsTableProps {
  rows: SubmissionRow[];
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  } catch {
    return iso;
  }
}

export function SubmissionsTable({ rows }: SubmissionsTableProps) {
  if (rows.length === 0) {
    return (
      <div className="text-center py-16 text-muted">
        <p className="text-lg font-medium">No submissions found</p>
        <p className="text-sm mt-1">Try adjusting the filters above</p>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto rounded-xl border border-border">
      <table className="min-w-full divide-y divide-border text-sm">
        <thead className="bg-gray-50">
          <tr>
            {['Date', 'Name', 'Department', 'Type', 'Request Title', 'Asana'].map((h) => (
              <th
                key={h}
                className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide"
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-100">
          {rows.map((row, i) => (
            <tr key={row.asanaTaskId || i} className="hover:bg-gray-50 transition-colors">
              <td className="px-4 py-3 text-gray-500 whitespace-nowrap">
                {formatDate(row.timestamp)}
              </td>
              <td className="px-4 py-3">
                <p className="font-medium text-gray-900">{row.name}</p>
                <p className="text-xs text-muted">{row.email}</p>
              </td>
              <td className="px-4 py-3 text-gray-700 whitespace-nowrap">
                {row.department || '—'}
              </td>
              <td className="px-4 py-3 whitespace-nowrap">
                <span
                  className={`inline-flex px-2 py-0.5 rounded-sm text-xs font-medium ${
                    FORM_TYPE_COLORS[row.formType] ?? 'bg-gray-100 text-gray-600'
                  }`}
                >
                  {FORM_TYPE_LABELS[row.formType] ?? row.formType}
                </span>
              </td>
              <td className="px-4 py-3 text-gray-700 max-w-[200px] truncate">
                {row.requestTitle || '—'}
              </td>
              <td className="px-4 py-3">
                {row.asanaTaskId ? (
                  <a
                    href={`https://app.asana.com/0/0/${row.asanaTaskId}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary hover:underline text-xs"
                  >
                    View ↗
                  </a>
                ) : (
                  <span className="text-muted">—</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
